import type { RouterOutputs } from "@repo/api";
import type { DeviceConnectionTypes } from "@repo/database";
import type { RealtimeChannel } from "@supabase/supabase-js";
import type { ReactNode } from "react";
import { useCallback, useEffect, useState } from "react";
import { PrinterLanguage } from "@repo/database";
import { REALTIME_SUBSCRIBE_STATES } from "@supabase/supabase-js";
import { useShallow } from "zustand/react/shallow";

import PrintRouteDialog from "~/components/print-route/print-route-dialog";
import PrinterDialog from "~/components/printer/printer-dialog";
import { usePlatform } from "~/hooks/use-platform";
import { PrintingContext } from "~/hooks/use-printer";
import { useUser } from "~/hooks/use-user";
import { api } from "~/lib/api";
import { supabase } from "~/lib/supabase";
import { useAlertStore } from "~/stores/use-alert-store";
import { useLoadingStore } from "~/stores/use-loading-store";
import { usePrinterStore } from "~/stores/use-printer-store";
import { UnsavedChangesProvider } from "./unsaved-changes-provider";

export type Printer = RouterOutputs["printer"]["getAllPrinters"][number];
export type PrintRoute = RouterOutputs["printer"]["getAllPrintRoutes"][number];

interface PrintingProviderProps {
  children: ReactNode;
}

export default function PrintingProvider({ children }: PrintingProviderProps) {
  const { data: printers, isFetching: isPrintersLoading } =
    api.printer.getAllPrinters.useQuery(undefined, {
      initialData: [],
    });

  const { user } = useUser();

  const { data: printRoutes, isFetching: isPrintRoutesLoading } =
    api.printer.getAllPrintRoutes.useQuery(undefined, {
      initialData: [],
    });

  const { setLoadingStage } = useLoadingStore(
    useShallow((state) => ({
      setLoadingStage: state.setCurrentLoadingStage,
    })),
  );

  const { showDialog } = useAlertStore(
    useShallow((state) => ({
      showDialog: state.showDialog,
    })),
  );

  const setSystemPrinters = usePrinterStore((state) => state.setSystemPrinters);

  const { platform } = usePlatform();

  const sendToPrinter = useCallback(
    (
      data: Uint8Array,
      printer: {
        name: string;
        ipAddress?: string;
        port?: number;
        connectionType: DeviceConnectionTypes;
        systemName?: string;
      },
    ) => {
      return new Promise<boolean>((resolve) => {
        const printerData = {
          init: {
            ipAddress: printer.ipAddress,
            port: printer.port,
            connectionType: printer.connectionType,
            systemName: printer.systemName,
          },
          data: Buffer.from(data).toString("base64"),
        };

        if (platform === "IOS" || platform === "ANDROID") {
          window.ReactNativeWebView?.postMessage(
            JSON.stringify({
              event: "print",
              data: printerData,
            }),
          );
          resolve(true);
        } else if (platform === "DESKTOP") {
          // Implement retry mechanism for desktop
          const maxRetries = 2;
          let retryCount = 0;

          const attemptPrint = () => {
            window.vrodex.printer
              .printData(printerData)
              .then((result) => {
                console.log(result);
                resolve(true);
              })
              .catch(async (e) => {
                retryCount++;
                console.error(`Print attempt ${retryCount} failed:`, e);

                if (retryCount <= maxRetries) {
                  console.log(
                    `Retrying print... Attempt ${retryCount + 1} of ${maxRetries + 1}`,
                  );
                  attemptPrint();
                } else {
                  console.error("All print attempts failed after retries");
                  // Show error to user
                  const result = await showDialog({
                    title: "Print Error",
                    message: `Failed to print the ticket at ${printer.name}. Check the connection and retry.`,
                    canClose: false,
                    buttons: [
                      {
                        label: "Cancel",
                        value: "cancel",
                        variant: "default",
                      },
                      {
                        label: "Retry",
                        value: "ok",
                        variant: "success",
                      },
                    ],
                  });

                  if (result === "ok") {
                    attemptPrint(); // Retry
                  }
                  resolve(false);
                }
              });
          };

          void attemptPrint(); // Start the first attempt
        } else {
          // NETWORK PRINT
          const printChannel = supabase.channel(`print-channel-${user.id}`);
          printChannel.send({
            type: "broadcast",
            event: "print",
            payload: printerData,
          });
        }
      });
    },
    [platform, showDialog],
  );

  const getPrinterLanguage = (lang: PrinterLanguage) => {
    switch (lang) {
      case PrinterLanguage.esc_pos:
        return "esc-pos";
      case PrinterLanguage.star_line:
        return "star-line";
      case PrinterLanguage.star_prnt:
        return "star-prnt";
      default:
        return "esc-pos";
    }
  };

  useEffect(() => {
    let desktopPrintChannel: RealtimeChannel | null = null;

    const subscribeToPrintChannel = () => {
      desktopPrintChannel = supabase.channel(`print-channel-${user.id}`);

      desktopPrintChannel
        .on<{ init: Printer; data: string }>(
          "broadcast",
          { event: "print" },
          (payload) => {
            console.log(payload);
            const buffer = Buffer.from(payload.payload.data, "base64");

            void sendToPrinter(buffer, payload.payload.init);
          },
        )
        .subscribe((status) => {
          if (REALTIME_SUBSCRIBE_STATES.SUBSCRIBED) {
            console.log("Printing channel subscribed");
          } else {
            console.error("Failed to subscribe to print channel");
          }
        });
    };

    if (platform === "DESKTOP") {
      subscribeToPrintChannel();
    }

    return () => {
      if (desktopPrintChannel) {
        supabase
          .removeChannel(desktopPrintChannel)
          .then(() => {
            console.log("Printing channel removed");
          })
          .catch((error) => {
            console.error("Error removing channel: ", error);
          });
      }
    };
  }, [platform, sendToPrinter, user.id]);

  // useEffect(() => {
  //   const channel = supabase.channel(`print-channel-${user.id}`);
  //   if (platform === "WEB" || platform === "PWA") {
  //     channel.subscribe((status) => {
  //       if (status !== REALTIME_SUBSCRIBE_STATES.SUBSCRIBED) {
  //         return null;
  //       }
  //     });

  //     setPrintChannel(channel);
  //   }

  //   return () => {
  //     channel.unsubscribe().catch((e) => console.error(e));
  //   };
  // }, [platform, user.id]);

  useEffect(() => {
    if (isPrintersLoading || isPrintRoutesLoading) {
      setLoadingStage("printers");
    } else {
      setLoadingStage(undefined);
    }
  }, [isPrintersLoading, isPrintRoutesLoading, setLoadingStage]);

  useEffect(() => {
    // todo detect device connection
    if (platform === "DESKTOP") {
      window.vrodex
        .getConnectedPrinters()
        .then((printers) => {
          setSystemPrinters(printers);
        })
        .catch((e) => console.error(e));
    }
  }, [platform, setSystemPrinters]);

  const context = {
    sendToPrinter,
    getPrinterLanguage,
    printers,
    printRoutes,
  };

  return (
    <PrintingContext.Provider value={context}>
      {children}
      <UnsavedChangesProvider>
        <PrinterDialog />
      </UnsavedChangesProvider>
      <UnsavedChangesProvider>
        <PrintRouteDialog />
      </UnsavedChangesProvider>
    </PrintingContext.Provider>
  );
}
